import { Store } from "@toeverything/infra";
import type { WorkspaceServerService } from "../../cloud";
import {
  teamsQuery,
  searchTeamByNameQuery,
  createTeamMutation,
  deleteTeamMutation, 
  updateTeamMutation,
  addTeamMembersMutation,
  removeTeamMemberMutation,
  syncTeamByWecomMutation,
  getMyTeamIdsQuery,
  loadTeamOrUserQuery
} from "@affine/graphql";

export class WorkspaceTeamsStore extends Store {
  constructor(private readonly workspaceServerService: WorkspaceServerService) {
    super();
  }

  async fetchTeams(workspaceId: string, signal?: AbortSignal) {
    if (!this.workspaceServerService.server) {
      throw new Error("No Server");
    }
    const data = await this.workspaceServerService.server.gql({
      query: teamsQuery,
      variables: {
        workspaceId,
      },
      context: {
        signal,
      },
    });

    return data.teams;
  }

  async createTeam(
    workspaceId: string,
    name: string,
    parentId?: string | null
  ) {
    if (!this.workspaceServerService.server) {
      throw new Error("No Server");
    }
    const data = await this.workspaceServerService.server.gql({
      query: createTeamMutation,
      variables: {
        input: {
          name,
          workspaceId,
          parentId,
        }
      }
    });

    return data;
  }

  async deleteTeam(id: string) {
    if (!this.workspaceServerService.server) {
      throw new Error("No Server");
    }
    const data = await this.workspaceServerService.server.gql({
      query: deleteTeamMutation,
      variables: {
        id,
      }
    });

    return data;
  }

  async updateTeam(id: string, name: string) {
    if (!this.workspaceServerService.server) {
      throw new Error("No Server");
    }
    const data = await this.workspaceServerService.server.gql({
      query: updateTeamMutation,
      variables: {
        id,
        name,
      }
    });

    return data;
  }

  async addTeamMembers(
    teamId: string,
    userIds: string[]
  ) {
    if (!this.workspaceServerService.server) {
      throw new Error("No Server");
    }
    const data = await this.workspaceServerService.server.gql({
      query: addTeamMembersMutation,
      variables: {
        teamId,
        userIds,
      }
    });

    return data;
  }

  async removeTeamMember(
    teamId: string,
    userId: string
  ) {
    if (!this.workspaceServerService.server) {
      throw new Error("No Server");
    }
    const data = await this.workspaceServerService.server.gql({
      query: removeTeamMemberMutation,
      variables: {
        teamId,
        userId,
      }
    });

    return data;
  }

  async syncTeam(workspaceId: string) {
    if (!this.workspaceServerService.server) {
      throw new Error("No Server");
    }
    const data = await this.workspaceServerService.server.gql({
      query: syncTeamByWecomMutation,
      variables: {
        workspaceId,
      },
      timeout: 5 * 60 * 1000,
    });

    return data.syncTeamByWecom;
  }

  async searchByName(workspaceId: string, name?: string, signal?: AbortSignal) {
    if (!this.workspaceServerService.server) {
      throw new Error("No Server");
    }
    const data = await this.workspaceServerService.server.gql({
      query: searchTeamByNameQuery,
      variables: {
        workspaceId,
        name: name ?? '',
      },
      context: {
        signal,
      },
    });

    return data.searchTeamByName;
  }

  async myTeamIds(workspaceId: string, signal?: AbortSignal) {
    if (!this.workspaceServerService.server) {
      throw new Error("No Server");
    }
    const data = await this.workspaceServerService.server.gql({
      query: getMyTeamIdsQuery,
      variables: {
        workspaceId,
      },
      context: {
        signal,
      },
    });

    return data.getMyTeamIds.ids;
  }

  async loadTeamOrUser(
    input: {
      id: string;
      type: "user" | "team";
    }[]
  ) {
    if (!this.workspaceServerService.server) {
      throw new Error("No Server");
    }
    const data = await this.workspaceServerService.server.gql({
      query: loadTeamOrUserQuery,
      variables: {
        input
      },
    });

    return data.loadTeamOrUser;
  }
}